import { useEffect, useState } from "react";
import { API_URL } from "src/utils/apiUrl";
import { captureError } from "src/utils/errorReporting";
import { useT } from "src/i18n";

// Verbatim current landing copy — used as initial state and as the failure fallback
// so first paint and any fetch failure still show correct copy (no layout shift).
// Keep in sync with puminet5api modules/betonijerry/coverageAreas.js FALLBACK_SENTENCE
// (snapshot of the live computed sentence at last copy sync, 2026-07-06). Finnish
// source text lives at src/i18n/fi.js common.coverageSentence.fallback.

/**
 * Pure: pick a usable sentence from the API JSON, else the localized fallback.
 * Exported for tests. `t` is threaded in explicitly — this function has no
 * hook access of its own (see src/i18n's shared procedure).
 */
export function resolveSentence(json, t) {
  const s = json && typeof json.sentence === "string" ? json.sentence.trim() : "";
  return s.length > 0 ? s : t("common.coverageSentence.fallback");
}

/**
 * Fetch the live coverage sentence for the landing page. Always resolves to a
 * usable sentence — fetch/parse failures keep the fallback and report to
 * Sentry (every fallback-returning catch reports, per betonijerry conventions).
 */
export function useCoverageSentence() {
  const t = useT();
  const fallback = t("common.coverageSentence.fallback");
  const [sentence, setSentence] = useState(fallback);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/betonijerry/coverage-areas`);
        if (!res.ok) throw new Error(`coverage-areas ${res.status}`);
        const json = await res.json();
        if (alive) setSentence(resolveSentence(json, t));
      } catch (error) {
        // A raw fetch rejection (offline / dropped connection / crawler abort)
        // surfaces as a TypeError ("Failed to fetch") and isn't an app bug — the
        // fallback sentence already covers it silently. Still report genuine HTTP
        // (!res.ok) and JSON-parse failures, which point at a real backend problem.
        const isNetworkFailure = error?.name === "TypeError" || error?.name === "AbortError";
        if (!isNetworkFailure) {
          captureError(error, { tags: { feature: "coverage-areas" } });
        }
        // keep the fallback either way
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [t]);

  return { sentence, loading };
}
